import React from 'react';
import {
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  TimePicker,
  Select,
  Switch,
  Tooltip
} from 'antd';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faEye,
  faEyeSlash,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import EventTypeBlockedDatesSection from './EventTypeBlockedDatesSection';
import MonthlyCapSection from './MonthlyCapSection';
import DailyCapacitySection from './DailyCapacitySection';
import {
  StyledModal,
  FormSection,
  CheckboxGrid
} from '../NewStyled';

const { TextArea } = Input;

const EventTypeFormModal = ({
  visible,
  editingEventType,
  form,
  selectedDays,
  setSelectedDays,
  monthlyCaps,
  setMonthlyCaps,
  defaultMonthlyCapEnabled,
  setDefaultMonthlyCapEnabled,
  defaultMonthlyCap,
  setDefaultMonthlyCap,
  dailyCapacityRules,
  setDailyCapacityRules,
  autoCloseNextMonth,
  setAutoCloseNextMonth,
  onSubmit,
  onCancel
}) => {
  return (
    <StyledModal
      title={editingEventType ? 'Edit Event Type' : 'Create Event Type'}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      width={750}
      okText={editingEventType ? 'Update' : 'Create'}
    >
      <FormSection>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{
            duration: 4,
            color: '#1890ff',
            maxBookingsPerDay: 10,
            advanceBookingDays: 30,
            bufferTime: 4,
            publiclyVisible: true,
            availableDays: [1, 2, 3, 4, 5],
            startTime: dayjs('09:00', 'HH:mm'),
            endTime: dayjs('19:00', 'HH:mm')
          }}
        >
          <Form.Item
            name="name"
            label="Event Type Name"
            rules={[{ required: true, message: 'Please enter event type name' }]}
          >
            <Input 
              placeholder="e.g., Health Screening" 
              size="large"
              style={{ height: '48px', fontSize: '16px', display: 'flex', alignItems: 'center' }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Brief description of this appointment type"
              style={{ minHeight: '96px', fontSize: '16px', lineHeight: '1.6', paddingTop: '12px' }}
            />
          </Form.Item>

          {/* Location Section */}
          <div style={{ 
            marginBottom: '24px', padding: '20px', background: '#f8f9fa', 
            borderRadius: '8px', border: '1px solid #e9ecef'
          }}>
            <h4 style={{ marginBottom: '16px', color: '#1a1a1a', fontSize: '16px', fontWeight: '600' }}>
              <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '8px', color: '#666' }} />
              Event Location
            </h4>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="city"
                  label="City"
                  tooltip="The city where this event type takes place. Used for volunteer registration filtering."
                >
                  <Input placeholder="e.g., Houston" size="large" style={{ height: '48px', fontSize: '16px' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="state"
                  label="State"
                  tooltip="The state where this event type takes place."
                >
                  <Input placeholder="e.g., TX" size="large" style={{ height: '48px', fontSize: '16px' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: '16px' }}>
              <Col span={24}>
                <Form.Item
                  name="timezone"
                  label="Timezone"
                  tooltip="Timezone for displaying check-in times in the patient queue. Defaults to Central Time (Texas)."
                  initialValue="America/Chicago"
                >
                  <Select size="large" style={{ height: '48px', fontSize: '16px' }} placeholder="Select timezone">
                    <Select.Option value="America/Chicago">Central Time (CT) - Texas, Chicago</Select.Option>
                    <Select.Option value="America/New_York">Eastern Time (ET) - New York, DC</Select.Option>
                    <Select.Option value="America/Denver">Mountain Time (MT) - Denver</Select.Option>
                    <Select.Option value="America/Los_Angeles">Pacific Time (PT) - Los Angeles</Select.Option>
                    <Select.Option value="America/Phoenix">Arizona Time - Phoenix (No DST)</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
              Setting a city, state, and timezone helps with location filtering and correct time display.
            </p>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="duration"
                label="Duration (hours)"
                rules={[{ required: true, message: 'Please enter duration' }]}
              >
                <InputNumber min={0.25} max={24} step={0.5} style={{ width: '100%', height: '48px', fontSize: '16px' }} size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="color"
                label="Color"
                rules={[{ required: true, message: 'Please select a color' }]}
              >
                <Input 
                  type="color" 
                  style={{ width: '100%', height: '48px', border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px', cursor: 'pointer' }} 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="maxBookingsPerDay" label="Max Bookings Per Day">
                <InputNumber min={1} max={100} style={{ width: '100%', height: '48px', fontSize: '16px' }} size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="advanceBookingDays"
                label="Advance Booking (days)"
                tooltip="Number of days in advance that bookings can be made. Set to 0 for unlimited."
              >
                <InputNumber min={0} max={365} style={{ width: '100%', height: '48px', fontSize: '16px' }} size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="bufferTime" label="Buffer Time Between Appointments (hours)">
            <InputNumber min={0} max={24} step={1} style={{ width: '100%', height: '48px', fontSize: '16px' }} size="large" />
          </Form.Item>

          {/* Visibility Settings */}
          <div style={{ 
            marginTop: '24px', padding: '20px', background: '#f0f9ff', 
            borderRadius: '8px', border: '1px solid #bae0ff'
          }}>
            <h4 style={{ marginBottom: '16px', color: '#1a1a1a', fontSize: '16px', fontWeight: '600' }}>
              <FontAwesomeIcon icon={faEye} style={{ marginRight: '8px', color: '#1890ff' }} />
              Visibility Settings
            </h4>
            
            <Form.Item
              name="publiclyVisible"
              label="Show on Public Booking Page"
              valuePropName="checked"
              tooltip="When enabled, this event type will be visible to the public on the booking page. When disabled, it will be hidden from public view but still accessible to admins."
            >
              <Switch 
                checkedChildren={<FontAwesomeIcon icon={faEye} />}
                unCheckedChildren={<FontAwesomeIcon icon={faEyeSlash} />}
              />
            </Form.Item>
            <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
              {editingEventType ? 
                'Toggle this setting to control whether this event type appears on the public booking page.' :
                'By default, new event types are visible on the public booking page.'
              }
            </p>
          </div>

          {/* Monthly Booking Caps Section */}
          <MonthlyCapSection
            eventTypeId={editingEventType?._id}
            defaultMonthlyCapEnabled={defaultMonthlyCapEnabled}
            defaultMonthlyCap={defaultMonthlyCap}
            monthlyCaps={monthlyCaps}
            onDefaultCapChange={(enabled, value) => {
              setDefaultMonthlyCapEnabled(enabled);
              setDefaultMonthlyCap(value);
            }}
            onMonthlyCapChange={(caps) => setMonthlyCaps(caps)}
            isEditing={!!editingEventType}
          />

          {/* Multiple Bookings Per Day (concurrent capacity) */}
          <DailyCapacitySection
            dailyCapacityRules={dailyCapacityRules}
            onChange={(rules) => setDailyCapacityRules(rules)}
          />

          {/* Auto-close next month (rolling cutoff) */}
          <div style={{
            marginTop: '24px', padding: '20px', background: '#f8f9fa',
            borderRadius: '8px', border: '1px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <h4 style={{ margin: 0, color: '#1a1a1a', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Auto-close next month
                  <Tooltip title="When on, the upcoming calendar month stops accepting new public bookings once the cutoff day has passed. The cutoff day is the last day the next month can still be booked. Admins can still book closed months.">
                    <FontAwesomeIcon icon={faQuestionCircle} style={{ color: '#8c8c8c', fontSize: '13px' }} />
                  </Tooltip>
                </h4>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '13px' }}>
                  Close the upcoming month after the cutoff day.
                </p>
              </div>
              <Switch
                checked={autoCloseNextMonth?.enabled === true}
                onChange={(checked) => setAutoCloseNextMonth({
                  ...autoCloseNextMonth,
                  enabled: checked,
                  cutoffDay: autoCloseNextMonth?.cutoffDay || 15
                })}
              />
            </div>

            {autoCloseNextMonth?.enabled && (
              <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: '#1a1a1a', fontSize: '14px' }}>Cutoff day of month:</span>
                <InputNumber
                  min={1}
                  max={28}
                  value={autoCloseNextMonth?.cutoffDay || 15}
                  onChange={(value) => setAutoCloseNextMonth({
                    ...autoCloseNextMonth,
                    cutoffDay: value || 15
                  })}
                />
                <span style={{ color: '#6b7280', fontSize: '13px' }}>
                  (last day the next month can be booked)
                </span>
              </div>
            )}
          </div>

          {/* Availability Section */}
          <div style={{ 
            marginTop: '24px', padding: '20px', background: '#f8f9fa', 
            borderRadius: '8px', border: '1px solid #e9ecef'
          }}>
            <h4 style={{ marginBottom: '20px', color: '#1a1a1a', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              Availability Schedule
              <Tooltip 
                title={
                  <div style={{ maxWidth: '300px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Configure When This Event is Available:</p>
                    <p style={{ margin: '0 0 8px 0' }}><strong>Available Days:</strong> Select which days of the week this event type can be booked.</p>
                    <p style={{ margin: '0 0 8px 0' }}><strong>Time Range:</strong> Set the start and end times for when bookings can be made.</p>
                    <p style={{ margin: 0 }}><strong>Tip:</strong> Use blocked dates below to close specific dates that fall on normally available days.</p>
                  </div>
                }
                placement="right"
                overlayStyle={{ maxWidth: '350px' }}
              >
                <FontAwesomeIcon 
                  icon={faQuestionCircle} 
                  style={{ color: '#8c8c8c', cursor: 'help', fontSize: '14px' }} 
                />
              </Tooltip>
            </h4>
            
            <Form.Item
              name="availableDays"
              label="Available Days"
              rules={[{ required: true, message: 'Please select at least one day' }]}
            >
              <CheckboxGrid>
                {[
                  { value: 0, abbr: 'SUN', full: 'Sunday', isWeekend: true },
                  { value: 1, abbr: 'MON', full: 'Monday', isWeekend: false },
                  { value: 2, abbr: 'TUE', full: 'Tuesday', isWeekend: false },
                  { value: 3, abbr: 'WED', full: 'Wednesday', isWeekend: false },
                  { value: 4, abbr: 'THU', full: 'Thursday', isWeekend: false },
                  { value: 5, abbr: 'FRI', full: 'Friday', isWeekend: false },
                  { value: 6, abbr: 'SAT', full: 'Saturday', isWeekend: true }
                ].map(day => {
                  const isSelected = selectedDays.includes(day.value);
                  return (
                    <div key={day.value} className="checkbox-item">
                      <div 
                        className={`day-button ${day.isWeekend ? 'weekend' : ''} ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          const newValue = isSelected 
                            ? selectedDays.filter(v => v !== day.value)
                            : [...selectedDays, day.value];
                          setSelectedDays(newValue);
                          form.setFieldValue('availableDays', newValue);
                        }}
                      >
                        <div className="day-abbr">{day.abbr}</div>
                        <div className="day-full">{day.full}</div>
                      </div>
                    </div>
                  );
                })}
              </CheckboxGrid>
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="startTime"
                  label="Start Time"
                  rules={[{ required: true, message: 'Please select start time' }]}
                >
                  <TimePicker 
                    format="h:mm A"
                    use12Hours={true}
                    style={{ width: '100%', height: '48px', fontSize: '16px' }}
                    size="large"
                    placeholder="Select start time"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="endTime"
                  label="End Time"
                  rules={[{ required: true, message: 'Please select end time' }]}
                >
                  <TimePicker 
                    format="h:mm A"
                    use12Hours={true}
                    style={{ width: '100%', height: '48px', fontSize: '16px' }}
                    size="large"
                    placeholder="Select end time"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Event-Specific Blocked Dates Section */}
          {editingEventType && (
            <EventTypeBlockedDatesSection
              eventTypeId={editingEventType._id}
              eventTypeName={editingEventType.name}
            />
          )}
        </Form>
      </FormSection>
    </StyledModal>
  );
};

export default EventTypeFormModal;
